/**
 * ReferralLandingPage Component
 *
 * Main page component for referral landing with routing integration.
 * Orchestrates all referral landing components and handles the complete user flow.
 */

import React from 'react';
import { useParams } from 'react-router';
import { useTheme } from '../../hooks/useTheme';
import { Layout } from '../layout/Layout';
import { TitleText } from '../ui/TitleText';
import { BodyText } from '../ui/BodyText';
import { useReferralToken } from '../../hooks/useReferralToken';
import { PhysicianCard } from './PhysicianCard';
import { DieticianCard } from './DieticianCard';
import { CallToActionSection } from './CallToActionSection';
import { PaymentGuidance } from '@/components/payment-guidance';
import { ReferralErrorFallback } from './ReferralErrorFallback';
import type { ReferralLandingPageProps } from '../../types/referralLanding';

/**
 * ReferralLandingPage component
 *
 * @param props - ReferralLandingPage component props
 * @returns JSX.Element
 */
export const ReferralLandingPage: React.FC<ReferralLandingPageProps> = ({
  className = '',
}) => {
  const { token } = useParams<{ token: string }>();
  const { isDark } = useTheme();

  // Fetch referral data using the custom hook
  const { data, isLoading, error, retry } = useReferralToken(token, {
    enableRetry: true,
    maxRetries: 3,
    retryDelay: 1000,
  });

  // Background color for the page
  const bgColor = isDark ? 'bg-secondary-900' : 'bg-white';

  // Handle error state
  if (error) {
    return (
      <Layout>
        <div className={`min-h-screen ${bgColor} py-8 px-4 ${className}`}>
          <div className="max-w-2xl mx-auto">
            <ReferralErrorFallback
              error={error}
              onRetry={retry}
              className="w-full"
            />
          </div>
        </div>
      </Layout>
    );
  }

  // Handle loading state
  if (isLoading || !data) {
    return (
      <Layout>
        <div className={`min-h-screen ${bgColor} py-8 px-4 ${className}`}>
          <div className="max-w-4xl mx-auto">
            <ReferralLandingPageSkeleton />
          </div>
        </div>
      </Layout>
    );
  }

  // Get full names for display
  const physicianName = `${data.physician.title} ${data.physician.firstName} ${data.physician.lastName}`;
  const dieticianName = `${data.dietician.firstName} ${data.dietician.lastName}`;

  return (
    <Layout>
      <div className={`min-h-screen ${bgColor} py-8 px-4 ${className}`}>
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Page header */}
          <div className="text-center">
            <TitleText level={1} className="mb-4">
              Welcome to Your Nutrition Care
            </TitleText>

            <BodyText variant="secondary" className="max-w-2xl mx-auto">
              You've been referred by {physicianName} to work with an Accredited Practicing Dietitian.
              Get started with personalised nutrition care tailored to your health needs.
            </BodyText>
          </div>

          {/* Healthcare provider cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Physician card */}
            <PhysicianCard
              physician={data.physician}
              isLoading={false}
              className="h-fit"
            />

            {/* Dietician card */}
            <DieticianCard
              dietician={data.dietician}
              isLoading={false}
              className="h-fit"
            />
          </div>

          {/* Fee guidance — below the cards, above the call to action (design §4.6, §4.13.2) */}
          <div className="max-w-2xl mx-auto">
            <PaymentGuidance guidance={data.paymentGuidance ?? null} />
          </div>

          {/* Call to action section */}
          <CallToActionSection
            referralToken={data.referralToken}
            physicianName={physicianName}
            dieticianName={dieticianName}
            className="max-w-2xl mx-auto"
          />
        </div>
      </div>
    </Layout>
  );
};

/**
 * Loading skeleton for the referral landing page
 */
const ReferralLandingPageSkeleton: React.FC = () => {
  const { isDark } = useTheme();
  const skeletonColor = isDark ? 'bg-secondary-700' : 'bg-secondary-200';

  return (
    <div className="space-y-8 animate-pulse">
      {/* Header skeleton */}
      <div className="text-center space-y-4">
        <div className={`h-10 ${skeletonColor} rounded w-96 mx-auto`} />
        <div className={`h-4 ${skeletonColor} rounded w-2/3 mx-auto`} />
        <div className={`h-4 ${skeletonColor} rounded w-1/2 mx-auto`} />
      </div>

      {/* Status indicator skeleton */}
      <div className="flex justify-center">
        <div className={`h-8 ${skeletonColor} rounded-full w-32`} />
      </div>

      {/* Cards skeleton */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className={`h-64 ${skeletonColor} rounded-lg`} />
        <div className={`h-80 ${skeletonColor} rounded-lg`} />
      </div>

      {/* CTA skeleton */}
      <div className="max-w-2xl mx-auto">
        <div className={`h-96 ${skeletonColor} rounded-lg`} />
      </div>

      {/* Footer skeleton */}
      <div className={`text-center pt-8 border-t ${isDark ? 'border-secondary-700' : 'border-secondary-200'}`}>
        <div className={`h-3 ${skeletonColor} rounded w-80 mx-auto`} />
      </div>
    </div>
  );
};

export default ReferralLandingPage;